import styled from 'styled-components';

import {Link} from '~/shared/router';
import {getLocalizationService} from '~/shared/services/localisationService';
import {getLegacySiteUrl} from '~/shared/services/urlService';
import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import {flipOnLTR, rotateOnLTR} from '~/shared/theme/flipOnLTR';
import {media} from '~/shared/theme/media';
import {body14Normal} from '~/shared/theme/typography';
import ScooberLogoIcon from '~/assets/images/scoober-logo-icon.svg?react';
import TapAndSaveIcon from '~/assets/images/icons/tapAndSaveIcon.svg?react';
import {User} from '~/shared/store/models';

import {ActionMenuLink} from './types';

const StyledScooberLogoIcon = styled(ScooberLogoIcon).attrs({
  className: 'avoid-fill-stroke',
})`
  width: 28px;
  height: 28px;
  ${flipOnLTR`margin-left: 5px;`}
  ${rotateOnLTR}
`;

const StyledTapAndSaveIcon = styled(TapAndSaveIcon).attrs({
  className: 'avoid-fill-stroke',
})`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  ${flipOnLTR`margin-left: 5px;`}
`;

const NewBadge = styled.span`
  ${body14Normal}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => theme.colors.supportPositive2};
  color: ${({theme}) => theme.colors.contentPositive};
  border-radius: 4px;
  padding: 0 4px;
  white-space: nowrap;
  ${flipOnLTR`margin-right: 8px;`}
`;

const ActionMenuItem = styled.div<{isVisibleOnMobile?: boolean; showOnMobileOnly?: boolean}>`
  display: ${({isVisibleOnMobile}) => (isVisibleOnMobile ? 'flex' : 'none')};
  align-items: center;
  color: ${({theme}) => theme.header.actionMenu.dropDown.textColor};
  background-color: ${({theme}) => theme.header.actionMenu.dropDown.backgroundColor};
  width: 100%;
  height: 48px;
  padding: 0 16px;
  cursor: pointer;

  &:hover {
    background-color: ${({theme}) => theme.header.actionMenu.dropDown.hover.backgroundColor};
    color: ${({theme}) => theme.colors.secondaryText};
  }

  ${media.minLargeMobile`
    display: ${({showOnMobileOnly}) => (showOnMobileOnly ? 'none' : 'flex')};
  `}

  ${media.minLargeTablet`
    margin: 0;
    &:first-child {
      border-top-right-radius: 12px;
      border-top-left-radius: 12px;
    }
  `}
`;

const ActionMenuText = styled.div<{isVisibleOnMobile?: boolean; isHighlighted?: boolean}>`
  ${body14Normal}
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 150px;
  font-weight: ${({isHighlighted}) => (isHighlighted ? 'bold' : 'normal')};
  color: inherit;
  ${media.minTablet`
    ${flipOnLTR`
      padding-right:20px;
    `}
  `}
`;

// fix outline around action menu
const MenuLink = styled(Link)<{isLast?: boolean}>`
  margin: 1px;
  color: inherit;
  height: 100%;
  width: 100%;
  ${flexCenterVertically}
  ${({isLast, theme}) => !isLast && `border-bottom: solid 1px ${theme.colors.background}`};

  &:hover {
    border-bottom: solid 1px transparent;
  }
`;

const Flex = styled.div`
  display: flex;
  flex: 1;
`;

interface ActionMenuLinksProps {
  links: ActionMenuLink[];
  onClick: (link: ActionMenuLink) => void;
  ignoreExtraBottom?: boolean;
  userData?: User;
  isIOS?: boolean;
  closeMenu: () => void;
}

const ActionMenuLinks = ({links, onClick, userData, closeMenu}: ActionMenuLinksProps) => {
  const {t} = getLocalizationService();

  const getMenuLinkProps = (link: ActionMenuLink) => {
    if (link.url) {
      return {
        target: link.openInNewWindow ? '_blank' : '',
        href: link.useLegacy ? getLegacySiteUrl(`${link.url}`) : link.url,
        hard: link.useLegacy,
        ...(link.rel && {rel: link.rel}),
        onClick: () => {
          onClick(link);
          if (link.toggleMenu) {
            closeMenu();
          }
        },
      };
    }
    
    return {
      'links-test': `action-menu-link-${link.textKey}`,
      ...(link.rel && {rel: link.rel}),
      onClick: () => {
        onClick(link);
        if (link.toggleMenu) {
          closeMenu();
        }
      },
    };
  };
    
  const modifiedLinks = links.filter((link: ActionMenuLink) => {
    if (!link.isAvailable) {
      return false;
    }
    if (link.loggedInUserOnly && !userData) {
      return false;
    }
    if (link.loggedOutUserOnly && userData) {
      return false;
    }
    if (link.compAdminOnly && (!userData || !userData.isCompanyAdmin)) {
      return false;
    }
    
    if (link.companyUserOnly === true) {
      // Show only to company users
      return userData && userData.isCompanyUser;
    }
    if (link.companyUserOnly === false) {
      // Show only to private users
      return userData && !userData.isCompanyUser;
    }
    
    return true;
  });

  return (
    <>
      {modifiedLinks.map((link: ActionMenuLink, index: number) => (
        <ActionMenuItem
          key={link.textKey}
          isVisibleOnMobile={link.isVisibleOnMobile}
          showOnMobileOnly={link.showOnMobileOnly}
          data-test-id={`ActionMenuItem_${link.textKey}`}
        >
          <MenuLink {...getMenuLinkProps(link)} isLast={index === modifiedLinks.length - 1}>
            <ActionMenuText isHighlighted={link.isHighlighted} isVisibleOnMobile={link.isVisibleOnMobile}>
              <Flex>
                {link.textKey === 'become_a_courier' && <StyledScooberLogoIcon />}
                {link.textKey === 'tap_and_save_menu_item' && <StyledTapAndSaveIcon />}
                {t(link.textKey)}
              </Flex>
              {link.isNew && <NewBadge>{t('New')}</NewBadge>}
            </ActionMenuText>
          </MenuLink>
        </ActionMenuItem>
      ))}
    </>
  );
};

export default ActionMenuLinks;
